
import React, { useState, useEffect, useRef } from 'react';
import { useTravelAutocomplete } from '../../hooks/useTravelAutocomplete';
import { MapPin, Plane, Train } from 'lucide-react';

const TravelAutocompleteInput = ({
    value,
    onChange,
    placeholder,
    className,
    onSelect,
    maxLength
}) => {
    const { suggestions, loading, search } = useTravelAutocomplete();
    const [showSuggestions, setShowSuggestions] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleChange = (e) => {
        const val = e.target.value;
        onChange(val);
        search(val);
        setShowSuggestions(true);
    };

    const handleSelect = (item) => {
        if (onSelect) {
            onSelect(item);
        } else {
            onChange(item.label || item.name || item.description);
        }
        setShowSuggestions(false);
    };

    const getIcon = (type) => {
        let IconComp = MapPin;
        let bgClass = "bg-gray-100";
        let textClass = "text-gray-600";

        if (type === 'station') {
            IconComp = Train;
            bgClass = "bg-orange-100";
            textClass = "text-orange-600";
        } else if (type === 'airport') {
            IconComp = Plane;
            bgClass = "bg-sky-100";
            textClass = "text-sky-600";
        }

        return (
            <div className={`w-9 h-9 rounded-full flex items-center justify-center ${bgClass} flex-shrink-0`}>
                <IconComp size={18} strokeWidth={2} className={textClass} />
            </div>
        );
    };

    return (
        <div className="relative w-full" ref={wrapperRef}>
            <input
                type="text"
                className={className}
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                maxLength={maxLength}
                onFocus={() => {
                    search(value);
                    setShowSuggestions(true);
                }}
            />

            {showSuggestions && suggestions.length > 0 && (
                <div
                    className="autocomplete-suggestions-container"
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: '8px',
                        zIndex: 1100,
                        background: '#fff',
                        border: '1px solid #f3f4f6',
                        borderRadius: '12px',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
                        maxHeight: '400px',
                        overflowY: 'auto',
                        padding: '8px 0'
                    }}
                >
                    {(!value || value.length < 1) ? (
                        <>
                            <div className="px-4 py-2 text-xs font-bold text-left text-gray-400 uppercase tracking-wider bg-gray-50/50 mb-1">
                                Most Searched Airports
                            </div>
                            {suggestions.filter(s => s.type === 'airport').map((item, index) => (
                                <div
                                    key={`air-${index}`}
                                    className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3 border-b border-gray-50 last:border-0 transition-all duration-200"
                                    onClick={() => handleSelect(item)}
                                >
                                    <div className="flex-shrink-0">
                                        {getIcon(item.type)}
                                    </div>
                                    <div className="flex flex-col min-w-0">
                                        <span className="font-semibold text-sm text-gray-900 leading-tight text-left">
                                            {item.label}
                                        </span>
                                        <span className="text-xs text-gray-500 mt-1 leading-tight text-left">
                                            {item.subLabel || item.city || "City"}
                                        </span>
                                    </div>
                                </div>
                            ))}
                            <div className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider bg-gray-50/50 mb-1 mt-2 border-t border-gray-100 pt-3 text-left">
                                Most Searched Stations
                            </div>
                            {suggestions.filter(s => s.type === 'station').map((item, index) => (
                                <div
                                    key={`stat-${index}`}
                                    className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3 border-b border-gray-50 last:border-0 transition-all duration-200"
                                    onClick={() => handleSelect(item)}
                                >
                                    <div className="flex-shrink-0">
                                        {getIcon(item.type)}
                                    </div>
                                    <div className="flex flex-col min-w-0">
                                        <span className="font-semibold text-sm text-gray-900 leading-tight text-left">
                                            {item.label}
                                        </span>
                                        <span className="text-xs text-gray-500 mt-1 leading-tight text-left">
                                            {item.subLabel || item.city || "City"}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </>
                    ) : (
                        suggestions.map((item, index) => (
                            <div
                                key={index}
                                className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3 border-b border-gray-50 last:border-0 transition-all duration-200"
                                onClick={() => handleSelect(item)}
                            >
                                <div className="flex-shrink-0">
                                    {getIcon(item.type)}
                                </div>
                                <div className="flex flex-col min-w-0">
                                    <span className="font-semibold text-sm text-gray-900 leading-tight text-left">
                                        {item.label}
                                    </span>
                                    <span className="text-xs text-gray-500 mt-1 leading-tight text-left">
                                        {item.subLabel || item.city || "City"}
                                    </span>
                                </div>
                            </div>
                        ))
                    )}
                    {loading && <div className="p-3 text-center text-gray-400 text-xs animate-pulse">Getting best routes...</div>}
                </div>
            )}
        </div>
    );
};

export default TravelAutocompleteInput;
